import {
  endOfMonth,
  format,
  isBefore,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths
} from 'date-fns'
import type {
  DolfinRpc,
  GetSubscriptionCompensationSourceRowsParams,
  HasRecentSubscriptionDataParams,
  SubscriptionCompensationAccountGroup,
  SubscriptionCompensationCell,
  SubscriptionCompensationMonthSummary,
  SubscriptionCompensationOverview,
  SubscriptionCompensationRuleBreakdown,
  SubscriptionCompensationSourceRow,
  SubscriptionCompensationSubscriptionRow
} from '../../rpc'

export const hasRecentSubscriptionData = (
  rpcClient: Pick<DolfinRpc, 'hasRecentSubscriptionData'>,
  params: HasRecentSubscriptionDataParams
) => rpcClient.hasRecentSubscriptionData(params)

const UNKNOWN_ACCOUNT_LABEL = 'Unknown account'

type MonthBucket = {
  key: string
  label: string
  start: Date
  end: Date
}

type AggregatedSubscription = {
  accountName: string
  subscriptionId: string
  status: string | null
  customerId: number | null
  providerCustomerId: string | null
  customerDisplayName: string | null
  startDate: string | null
  endDate: string | null
  isOngoing: boolean
  wholeSubscriptionTotal: number
  rows: SubscriptionCompensationSourceRow[]
}

const getMonthBucketKey = (date: string) => format(startOfMonth(parseISO(date)), 'yyyy-MM-dd')

const getRowReferenceMonth = (
  row: Pick<SubscriptionCompensationSourceRow, 'statementPeriodStart' | 'effectiveDate'>
) => row.statementPeriodStart ?? row.effectiveDate

const buildMonthBuckets = (months: 12 | 24, referenceDate: Date): MonthBucket[] => {
  const currentMonthStart = startOfMonth(referenceDate)

  return Array.from({ length: months }, (_, index) => {
    const monthStart = subMonths(currentMonthStart, months - index - 1)

    return {
      key: format(monthStart, 'yyyy-MM-dd'),
      label: format(monthStart, 'MMM yyyy'),
      start: monthStart,
      end: endOfMonth(monthStart)
    }
  })
}

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0)

const compareLabel = (left: string, right: string) =>
  left.localeCompare(right, undefined, { sensitivity: 'base' })

const getCustomerDisplayLabel = (
  row: Pick<
    SubscriptionCompensationSourceRow,
    'customerId' | 'providerCustomerId' | 'customerDisplayName' | 'accountName'
  >
) =>
  row.accountName?.trim() ||
  row.customerDisplayName?.trim() ||
  row.providerCustomerId ||
  (row.customerId != null ? `#${row.customerId}` : UNKNOWN_ACCOUNT_LABEL)

const getAccountKey = (
  row: Pick<SubscriptionCompensationSourceRow, 'customerId' | 'providerCustomerId' | 'accountName'>
) =>
  row.customerId != null
    ? `customer:${row.customerId}`
    : row.providerCustomerId
      ? `provider-customer:${row.providerCustomerId}`
      : (row.accountName ?? UNKNOWN_ACCOUNT_LABEL)

const isTerminalSubscriptionStatus = (status: string | null) =>
  !!status &&
  ['canceled', 'cancelled', 'finished', 'ended', 'inactive', 'transferred'].includes(
    status.trim().toLowerCase()
  )

export const getSubscriptionLossMonth = (
  { endDate }: Pick<SubscriptionCompensationSubscriptionRow, 'endDate'>,
  now = new Date()
) => {
  if (!endDate) {
    return null
  }

  const parsedEndDate = parseISO(endDate)

  if (!isValid(parsedEndDate) || !isBefore(parsedEndDate, startOfDay(now))) {
    return null
  }

  return format(startOfMonth(parsedEndDate), 'yyyy-MM-dd')
}

const normalizeStatus = (statuses: string[] | undefined) => {
  const normalized = (statuses ?? []).map(status => status.trim()).filter(Boolean)

  return normalized.length > 0 ? normalized : undefined
}

export default class SubscriptionsDomain {
  private readonly rpcClient: Pick<DolfinRpc, 'getSubscriptionCompensationSourceRows'>
  private readonly now: Date

  constructor({
    rpcClient,
    now = new Date()
  }: {
    rpcClient: Pick<DolfinRpc, 'getSubscriptionCompensationSourceRows'>
    now?: Date
  }) {
    this.rpcClient = rpcClient
    this.now = now
  }

  async getRepSubscriptionCompensationOverview(
    params: GetSubscriptionCompensationSourceRowsParams
  ): Promise<SubscriptionCompensationOverview> {
    const normalizedFilters = {
      ...params.filters,
      statuses: normalizeStatus(params.filters?.statuses),
      search: params.filters?.search?.trim() || undefined
    }

    const rows = await this.rpcClient.getSubscriptionCompensationSourceRows({
      ...params,
      filters: normalizedFilters
    })

    const referenceDate = params.referenceDate ? parseISO(params.referenceDate) : new Date()
    const months = buildMonthBuckets(params.months, referenceDate)
    const visibleMonthKeys = new Set(months.map(month => month.key))

    const groupedSubscriptions = new Map<string, AggregatedSubscription>()

    for (const row of rows) {
      const subscriptionKey = `${getAccountKey(row)}::${row.subscriptionId}`
      const existing = groupedSubscriptions.get(subscriptionKey)
      const nextWholeSubscriptionTotal = (existing?.wholeSubscriptionTotal ?? 0) + row.amount

      if (!existing) {
        groupedSubscriptions.set(subscriptionKey, {
          accountName: getCustomerDisplayLabel(row),
          subscriptionId: row.subscriptionId,
          status: row.subscriptionStatus ?? null,
          customerId: row.customerId,
          providerCustomerId: row.providerCustomerId,
          customerDisplayName: row.customerDisplayName,
          startDate: row.subscriptionStartDate ?? getRowReferenceMonth(row),
          endDate: row.subscriptionEndDate ?? null,
          isOngoing: !isTerminalSubscriptionStatus(row.subscriptionStatus),
          wholeSubscriptionTotal: nextWholeSubscriptionTotal,
          rows: [row]
        })
        continue
      }

      existing.rows.push(row)
      existing.wholeSubscriptionTotal = nextWholeSubscriptionTotal
      existing.customerId = existing.customerId ?? row.customerId
      existing.providerCustomerId = existing.providerCustomerId ?? row.providerCustomerId
      existing.customerDisplayName = existing.customerDisplayName ?? row.customerDisplayName
      existing.accountName =
        existing.accountName === UNKNOWN_ACCOUNT_LABEL
          ? getCustomerDisplayLabel(row)
          : existing.accountName
      existing.startDate =
        [existing.startDate, row.subscriptionStartDate ?? getRowReferenceMonth(row)]
          .filter((value): value is string => !!value)
          .sort()[0] ?? null
      const endDates = [existing.endDate, row.subscriptionEndDate]
        .filter((value): value is string => !!value)
        .sort()
      existing.endDate = endDates[endDates.length - 1] ?? null
      existing.status = row.subscriptionStatus ?? existing.status
      existing.isOngoing =
        existing.isOngoing && !isTerminalSubscriptionStatus(row.subscriptionStatus)
    }

    const includedSubscriptions = [...groupedSubscriptions.values()]
      .map(subscription => this.buildSubscriptionRow(subscription, months))
      .filter(subscription =>
        subscription.cells.some(cell => visibleMonthKeys.has(cell.month) && cell.total > 0)
      )
      .sort(
        (left, right) =>
          compareLabel(left.accountName, right.accountName) ||
          compareLabel(left.subscriptionId, right.subscriptionId)
      )

    const accountGroupsMap = new Map<string, SubscriptionCompensationSubscriptionRow[]>()

    for (const subscription of includedSubscriptions) {
      const current = accountGroupsMap.get(subscription.accountName) ?? []
      current.push(subscription)
      accountGroupsMap.set(subscription.accountName, current)
    }

    const accountGroups = [...accountGroupsMap.entries()]
      .map(([accountName, subscriptions]) =>
        this.buildAccountGroup(accountName, subscriptions, months)
      )
      .sort((left, right) => compareLabel(left.accountName, right.accountName))

    const monthSummaries = this.buildMonthSummaries(months, accountGroups)

    return {
      organizationId: params.organizationId,
      profileId: params.profileId ?? null,
      periodMonths: params.months,
      generatedAt: new Date().toISOString(),
      months: monthSummaries,
      accountGroups,
      filters: normalizedFilters
    }
  }

  private buildSubscriptionRow(
    subscription: AggregatedSubscription,
    months: MonthBucket[]
  ): SubscriptionCompensationSubscriptionRow {
    const rowsByVisibleMonth = new Map<string, SubscriptionCompensationSourceRow[]>()

    for (const row of subscription.rows) {
      const monthKey = getMonthBucketKey(getRowReferenceMonth(row))
      if (!rowsByVisibleMonth.has(monthKey)) {
        rowsByVisibleMonth.set(monthKey, [])
      }
      rowsByVisibleMonth.get(monthKey)!.push(row)
    }

    const cells = months.map(month =>
      this.buildCell(month.key, rowsByVisibleMonth.get(month.key) ?? [])
    )
    const visibleTotals = cells.map(cell => cell.total).filter(total => total > 0)
    const firstVisibleMonth = months[0]?.key
    const lastVisibleMonth = months[months.length - 1]?.key
    return {
      accountName: subscription.accountName,
      subscriptionId: subscription.subscriptionId,
      status: subscription.status,
      customerId: subscription.customerId,
      providerCustomerId: subscription.providerCustomerId,
      customerDisplayName: subscription.customerDisplayName,
      visibleTotal: sum(cells.map(cell => cell.total)),
      wholeSubscriptionTotal: subscription.wholeSubscriptionTotal,
      startDate: subscription.startDate,
      endDate: subscription.endDate,
      isOngoing: subscription.isOngoing,
      continuesBeforeVisibleRange:
        !!firstVisibleMonth &&
        subscription.rows.some(
          row => getMonthBucketKey(getRowReferenceMonth(row)) < firstVisibleMonth
        ),
      continuesAfterVisibleRange:
        !!lastVisibleMonth &&
        subscription.rows.some(
          row => getMonthBucketKey(getRowReferenceMonth(row)) > lastVisibleMonth
        ),
      averageVisibleMonthlyTotal:
        visibleTotals.length > 0 ? sum(visibleTotals) / visibleTotals.length : 0,
      cells
    }
  }

  private buildCell(
    month: string,
    rows: SubscriptionCompensationSourceRow[]
  ): SubscriptionCompensationCell {
    const breakdownsByRule = new Map<string, SubscriptionCompensationRuleBreakdown>()

    for (const row of rows) {
      const key = String(row.compensationRuleId ?? 'unassigned')
      const current = breakdownsByRule.get(key)
      const label = row.compensationRuleLabel?.trim() || 'Manual adjustment'

      if (!current) {
        breakdownsByRule.set(key, {
          compensationRuleId: row.compensationRuleId,
          compensationRuleLabel: label,
          total: row.amount,
          detailHref: row.compensationRuleId != null ? `/dashboard?page=plans` : null,
          dealId: row.dealId,
          payoutId: row.payoutId
        })
        continue
      }

      current.total += row.amount
      current.dealId = current.dealId ?? row.dealId
      current.payoutId = current.payoutId ?? row.payoutId
    }

    return {
      month,
      total: sum(rows.map(row => row.amount)),
      breakdown: [...breakdownsByRule.values()].sort((left, right) =>
        compareLabel(left.compensationRuleLabel, right.compensationRuleLabel)
      )
    }
  }

  private buildAccountGroup(
    accountName: string,
    subscriptions: SubscriptionCompensationSubscriptionRow[],
    months: MonthBucket[]
  ): SubscriptionCompensationAccountGroup {
    return {
      accountName,
      subscriptionCount: subscriptions.length,
      monthlyTotals: months.map(month => ({
        month: month.key,
        total: sum(
          subscriptions.map(
            subscription => subscription.cells.find(cell => cell.month === month.key)?.total ?? 0
          )
        )
      })),
      subscriptions
    }
  }

  private buildMonthSummaries(
    months: MonthBucket[],
    accountGroups: SubscriptionCompensationAccountGroup[]
  ): SubscriptionCompensationMonthSummary[] {
    let previousAccountNames = new Set<string>()
    let previousSubscriptionIds = new Set<string>()
    const subscriptions = accountGroups.flatMap(accountGroup =>
      accountGroup.subscriptions.map(subscription => ({
        accountName: accountGroup.accountName,
        subscription,
        lossMonth: getSubscriptionLossMonth(subscription, this.now)
      }))
    )

    return months.map(month => {
      const activeAccounts = new Set<string>()
      const activeSubscriptions = new Set<string>()

      for (const accountGroup of accountGroups) {
        const accountTotal =
          accountGroup.monthlyTotals.find(total => total.month === month.key)?.total ?? 0

        if (accountTotal > 0) {
          activeAccounts.add(accountGroup.accountName)
        }

        for (const subscription of accountGroup.subscriptions) {
          const cellTotal = subscription.cells.find(cell => cell.month === month.key)?.total ?? 0

          if (cellTotal > 0) {
            activeSubscriptions.add(subscription.subscriptionId)
          }
        }
      }

      const newAccountCount = [...activeAccounts].filter(
        accountName => !previousAccountNames.has(accountName)
      ).length
      const newSubscriptionCount = [...activeSubscriptions].filter(
        subscriptionId => !previousSubscriptionIds.has(subscriptionId)
      ).length
      const endDateLostSubscriptionIds = new Set(
        subscriptions
          .filter(({ lossMonth }) => lossMonth === month.key)
          .map(({ subscription }) => subscription.subscriptionId)
      )
      const lostSubscriptionCount = endDateLostSubscriptionIds.size
      const lostAccountCount = new Set(
        accountGroups
          .filter(accountGroup => {
            const accountSubscriptions = subscriptions.filter(
              subscription => subscription.accountName === accountGroup.accountName
            )

            return (
              accountSubscriptions.some(subscription => subscription.lossMonth === month.key) &&
              accountSubscriptions.every(
                subscription =>
                  subscription.lossMonth != null && subscription.lossMonth <= month.key
              )
            )
          })
          .map(accountGroup => accountGroup.accountName)
      ).size

      const summary: SubscriptionCompensationMonthSummary = {
        month: month.key,
        label: month.label,
        total: sum(
          accountGroups.map(
            accountGroup =>
              accountGroup.monthlyTotals.find(total => total.month === month.key)?.total ?? 0
          )
        ),
        accountCount: activeAccounts.size,
        subscriptionCount: activeSubscriptions.size,
        newAccountCount,
        lostAccountCount,
        newSubscriptionCount,
        lostSubscriptionCount
      }

      previousAccountNames = activeAccounts
      previousSubscriptionIds = activeSubscriptions

      return summary
    })
  }
}
